import { useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { GeschaeftspartnerLogo } from "@/components/GeschaeftspartnerLogo";
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ClockIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  TagIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { RenameMaintenanceCampaignDialog } from "./RenameMaintenanceCampaignDialog";
import { maintenanceCampaignProgress } from "@/lib/wartungen";
import { projectWorkflowStatusBadgeClass } from "@/lib/projects";
import type { CampaignView } from "../lib/campaignView";
import type { CampaignAverageTime } from "../lib/useCampaignAverageTimes";
import { MaintenanceProgressBar } from "./MaintenanceProgressBar";

export interface MaintenanceCampaignCardProps {
  view: CampaignView;
  /** Kunde in der Karte anzeigen (in gruppierten Listen meist überflüssig). */
  showCustomer?: boolean;
  /** Vertragsnummer anzeigen (auf der Vertragsseite überflüssig). */
  showContract?: boolean;
  /** Durchschnittliche Zeit pro gewartetem Objekt – nur für Büromitarbeiter. */
  averagePerAsset?: CampaignAverageTime | null;
  className?: string;
}

function MetaItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 text-sm text-label-secondary" title={`${label}: ${value}`}>
      <span className="shrink-0 text-label-tertiary" aria-hidden>
        {icon}
      </span>
      <span className="truncate">{value}</span>
    </div>
  );
}

/**
 * Karte einer einzelnen Wartung (Kampagne). Die gesamte Karte ist klickbar
 * und führt in die Wartungsansicht mit Objektliste und Plan.
 */
export function MaintenanceCampaignCard({
  view,
  showCustomer = true,
  showContract = true,
  averagePerAsset = null,
  className,
}: MaintenanceCampaignCardProps) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const title = titleOverride ?? view.title;
  const campaignId = Number(view.campaignId);
  const canRename = Number.isFinite(campaignId) && campaignId > 0 && view.campaign.project_completed !== true;
  const progress = maintenanceCampaignProgress(view.campaign);
  const partnerId = Number(view.contract.customer_geschaeftspartner_id);
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <GeschaeftspartnerLogo
            partnerId={partnerId}
            name={view.customer.label}
            hasLogo={view.contract.customer_has_logo}
            logoUpdatedAt={view.contract.customer_logo_updated_at}
            className="h-10 w-10 rounded-xl text-xs"
          />
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1">
              <h3 className="truncate text-base font-semibold text-label" title={title}>
                {title}
              </h3>
              {canRename ? (
                <button
                  type="button"
                  className="pointer-events-auto shrink-0 rounded-md p-1 text-label-tertiary hover:bg-fill-tertiary hover:text-label"
                  aria-label="Namen der Wartung ändern"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setRenameOpen(true);
                  }}
                >
                  <PencilSquareIcon className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </div>
            <p className="mt-0.5 truncate text-sm text-label-secondary">
              {view.category.label}
              {view.scheduleLabel !== "–" ? ` · ${view.scheduleLabel}` : ""}
            </p>
          </div>
        </div>
        <span className={clsx(projectWorkflowStatusBadgeClass(view.workflow.key), "shrink-0")}>
          {view.workflow.label}
        </span>
      </div>

      <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
        <MetaItem icon={<BuildingOffice2Icon className="h-4 w-4" />} label="Ort" value={view.location.label} />
        <MetaItem icon={<CalendarDaysIcon className="h-4 w-4" />} label="Zeitraum" value={view.scheduleLabel} />
        {showCustomer ? (
          <MetaItem icon={<UserIcon className="h-4 w-4" />} label="Kunde" value={view.customer.label} />
        ) : null}
        {showContract ? (
          <MetaItem
            icon={<DocumentTextIcon className="h-4 w-4" />}
            label={view.contractRelationLabel}
            value={view.contractLabel}
          />
        ) : null}
        {!showCustomer && !showContract ? (
          <MetaItem icon={<TagIcon className="h-4 w-4" />} label="Kategorie" value={view.category.label} />
        ) : null}
      </div>

      <div className="mt-auto pt-3">
        {progress ? (
          <MaintenanceProgressBar
            completed={progress.completed}
            total={progress.total}
            countLabel={`${progress.completed} von ${progress.total} Objekten gewartet`}
          />
        ) : (
          <p className="text-xs text-label-tertiary">Noch keine Objekte erfasst</p>
        )}
        {averagePerAsset ? (
          <p
            className="mt-2 flex items-center gap-1.5 text-xs text-label-secondary"
            title={averagePerAsset.detail}
          >
            <ClockIcon className="h-3.5 w-3.5 shrink-0 text-label-tertiary" aria-hidden />
            <span className="truncate">
              {averagePerAsset.loading ? "Zeit pro Objekt wird ermittelt …" : averagePerAsset.label}
            </span>
          </p>
        ) : null}
      </div>

      {view.href ? (
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent">
          Wartung öffnen
          <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
        </span>
      ) : (
        <span className="mt-3 text-xs text-label-tertiary">
          Nicht öffenbar: Projekt oder Kategorie fehlt.
        </span>
      )}
    </>
  );

  const baseClass = clsx(
    "group relative flex h-full flex-col rounded-ios border border-separator bg-card p-4 text-left shadow-ios transition",
    view.href && "hover:border-accent/50 hover:shadow-ios-md",
    className,
  );

  return (
    <div className={baseClass}>
      {view.href ? (
        <Link
          to={view.href}
          className="absolute inset-0 z-0 rounded-ios focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={`${title} öffnen`}
        />
      ) : null}
      <div className={clsx("relative z-10 flex min-h-0 flex-1 flex-col", view.href && "pointer-events-none")}>{body}</div>
      {canRename ? (
        <RenameMaintenanceCampaignDialog
          open={renameOpen}
          campaignId={campaignId}
          initialName={title}
          onClose={() => setRenameOpen(false)}
          onSaved={setTitleOverride}
        />
      ) : null}
    </div>
  );
}
